import React from 'react';
import { t } from '../../translation';
import CollapsibleSection from './collapsible-section';

interface DamageToggleProps {
    className?: string;
    visible?: boolean;
    skipToggle?: boolean;
    isActive?: boolean;
}

interface SphereEntry {
    name: string;
    index: number;
    source: 'sphere' | 'camera';
}

interface SelectedSphere {
    name: string;
    description: string;
    index: number;
    source: 'sphere' | 'camera';
    coords?: string;
}

const DamageToggle: React.FC<DamageToggleProps> = ({ className = '', visible = true, skipToggle = false, isActive = false }) => {
    const [enabled, setEnabled] = React.useState(false);
    const [spheres, setSpheres] = React.useState<SphereEntry[]>([]);
    const [selectedSphere, setSelectedSphere] = React.useState<SelectedSphere | null>(null);

    // Poll PositionSphereButtons state to keep selector in sync
    React.useEffect(() => {
        if (!enabled && !skipToggle) return;
        if (!(enabled || skipToggle)) return;
        const interval = setInterval(() => {
            const comp = window.viewer?.positionSphereButtonsComponent;
            if (comp) {
                if (comp.state.spheres.length !== spheres.length) {
                    setSpheres([...comp.state.spheres]);
                }
                const compSelected = comp.state.selectedSphere;
                if (compSelected && compSelected.name !== selectedSphere?.name) {
                    setSelectedSphere({ ...compSelected });
                } else if (!compSelected && selectedSphere) {
                    setSelectedSphere(null);
                }
            }
        }, 200);
        return () => clearInterval(interval);
    }, [enabled, skipToggle, spheres.length, selectedSphere?.name]);

    // Auto-enable when isActive becomes true (panel controlled externally)
    React.useEffect(() => {
        if (isActive && skipToggle) {
            setEnabled(true);
            toggleSpheresVisibility(true);
            const list = buildSphereList();
            setSpheres(list);
            const comp = window.viewer?.positionSphereButtonsComponent;
            if (comp && !comp.state.isListVisible) {
                comp.setState({ isListVisible: true, spheres: list });
            }
            window.viewer?.renderNextFrame?.();
        } else if (!isActive && skipToggle && enabled) {
            setEnabled(false);
            toggleSpheresVisibility(false);
            setSelectedSphere(null);
            setSpheres([]);
            const comp = window.viewer?.positionSphereButtonsComponent;
            if (comp && comp.state.isListVisible) {
                comp.setState({ isListVisible: false, selectedSphere: null });
            }
            window.viewer?.highlightCameraDamageSelection?.(null);
            window.viewer?.clearCameraDamageNavigationFilter?.();
            window.viewer?.renderNextFrame?.();
        }
    }, [isActive, skipToggle]);

    const toggleSpheresVisibility = (visible: boolean) => {
        if (window.viewer) {
            const allSpheres = window.viewer.getDamageSphereRenderComponents?.() || [];
            allSpheres.forEach((sphere: any) => {
                sphere.entity.enabled = visible;
            });
        }
    };

    const buildSphereList = (): SphereEntry[] => {
        if (!window.viewer || typeof window.viewer.selectedPointerSpawner !== 'function') return [];
        const { nameSpheres } = window.viewer.selectedPointerSpawner();
        const sphereEntries: SphereEntry[] = nameSpheres.map((name: string, index: number) => ({
            name, index, source: 'sphere' as const
        }));
        const cameraEntries: SphereEntry[] = (window.viewer.cameraDamageRecords || []).map((damage: any, index: number) => ({
            name: `[CAM] ${damage.name} - ${damage.selectValue || '-'}`,
            index,
            source: 'camera' as const
        }));
        return [...sphereEntries, ...cameraEntries];
    };

    const handleToggle = () => {
        const newEnabled = !enabled;
        setEnabled(newEnabled);

        if (newEnabled) {
            toggleSpheresVisibility(true);
            const list = buildSphereList();
            setSpheres(list);
            // Also sync PositionSphereButtons state so it knows list is visible
            const comp = window.viewer?.positionSphereButtonsComponent;
            if (comp && !comp.state.isListVisible) {
                comp.setState({ isListVisible: true, spheres: list });
            }
        } else {
            toggleSpheresVisibility(false);
            setSelectedSphere(null);
            setSpheres([]);
            // Sync PositionSphereButtons to hide state
            const comp = window.viewer?.positionSphereButtonsComponent;
            if (comp && comp.state.isListVisible) {
                comp.setState({ isListVisible: false, selectedSphere: null });
            }
            window.viewer?.highlightCameraDamageSelection?.(null);
            window.viewer?.clearCameraDamageNavigationFilter?.();
        }

        window.viewer?.renderNextFrame?.();
    };

    const handleSphereSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const selectedName = e.target.value;
        if (!selectedName) {
            setSelectedSphere(null);
            window.viewer?.highlightCameraDamageSelection?.(null);
            window.viewer?.clearCameraDamageNavigationFilter?.();
            const comp = window.viewer?.positionSphereButtonsComponent;
            if (comp) comp.setState({ selectedSphere: null });
            return;
        }

        const entry = spheres.find(s => s.name === selectedName);
        if (!entry || !window.viewer) return;

        if (entry.source === 'camera') {
            const damage = window.viewer.cameraDamageRecords?.[entry.index];
            const cameraIds = damage?.cameraIds || (damage?.cameraId ? [damage.cameraId] : []);
            window.viewer.highlightCameraDamageSelection?.(cameraIds);
            window.viewer.setCameraDamageNavigationFilter?.(cameraIds);

            const sel: SelectedSphere = {
                name: selectedName,
                description: damage?.description || 'No description',
                index: entry.index,
                source: 'camera'
            };
            setSelectedSphere(sel);
            const comp = window.viewer.positionSphereButtonsComponent;
            if (comp) comp.setState({ selectedSphere: sel });
            return;
        }

        // Sphere source
        window.viewer.highlightCameraDamageSelection?.(null);
        window.viewer.clearCameraDamageNavigationFilter?.();
        const description = window.viewer.descriptionsPointer?.[entry.index] || 'No description';
        const allSpheres = window.viewer.getDamageSphereRenderComponents?.() || [];
        const sphereEntity = allSpheres.find((s: any) => s.entity.name === selectedName);
        if (sphereEntity) {
            const pos = sphereEntity.entity.getPosition();
            window.viewer.editingSpherePos = pos.clone();
            window.viewer.moveCameraToSphere(entry.index);
        }

        const sel: SelectedSphere = {
            name: selectedName,
            description,
            index: entry.index,
            source: 'sphere'
        };
        setSelectedSphere(sel);
        const comp = window.viewer.positionSphereButtonsComponent;
        if (comp) comp.setState({ selectedSphere: sel });
    };

    const handleRefresh = () => {
        const list = buildSphereList();
        setSpheres(list);
        const comp = window.viewer?.positionSphereButtonsComponent;
        if (comp) {
            comp.setState({ spheres: list });
        }
    };

    const handleEdit = () => {
        if (!window.viewer || !selectedSphere) return;

        if (selectedSphere.source === 'camera') {
            const damage = window.viewer.cameraDamageRecords?.[selectedSphere.index];
            const cameraId = damage?.cameraIds?.[0] || damage?.cameraId || null;
            if (cameraId) {
                window.viewer.openCameraDamageEditor?.(cameraId);
            }
            return;
        }

        const sphereSelector = document.getElementById('sphere-select') as HTMLSelectElement;
        if (sphereSelector) {
            const syntheticEvent = { target: sphereSelector } as React.ChangeEvent<HTMLSelectElement>;
            window.viewer.editPointerSpawner?.(syntheticEvent);
        }
    };

    const handleViewPhotos = () => {
        if (!window.viewer || !selectedSphere) return;

        if (selectedSphere.source === 'camera') {
            const damage = window.viewer.cameraDamageRecords?.[selectedSphere.index];
            const cameraIds = damage?.cameraIds || (damage?.cameraId ? [damage.cameraId] : []);
            if (cameraIds.length > 0 && (window as any).openDamageCameraCarousel) {
                (window as any).openDamageCameraCarousel(cameraIds, damage?.id || '');
                return;
            }
        }

        // Fallback: open photo gallery via PositionSphereButtons
        const comp = window.viewer?.positionSphereButtonsComponent;
        if (comp) {
            // Ensure comp has the right selectedSphere before calling
            comp.setState({ selectedSphere: { ...selectedSphere } }, () => {
                comp.handleViewDamagePhotos();
            });
        }
    };

    const getPhotoCount = (): number => {
        if (!window.viewer || !selectedSphere) return 0;
        if (selectedSphere.source === 'camera') {
            return (window.viewer.cameraDamageRecords?.[selectedSphere.index]?.photoIds || []).length;
        }
        return window.viewer.getPhotosForDamage?.(selectedSphere.index)?.length || 0;
    };

    const getSide = (): string => {
        if (!selectedSphere) return '-';
        if (selectedSphere.source === 'camera') {
            return window.viewer?.cameraDamageRecords?.[selectedSphere.index]?.selectValue || '-';
        }
        return window.viewer?.selectPointer?.[selectedSphere.index] || '-';
    };

    const photoCount = getPhotoCount();

    if (!visible) return null;

    const effectivelyActive = enabled || skipToggle;

    return (
        <div className={`damage-toggle-section ${className}`}>
            {/* Toggle switch - only when not externally controlled */}
            {!skipToggle && (
                <div className="orbital-toggle">
                    <span className="orbital-toggle-label">{t('ui.damageToggle')}:</span>
                    <button
                        type="button"
                        className={`orbital-toggle-button${enabled ? ' orbital-toggle-button--active' : ''}`}
                        onClick={handleToggle}
                        role="switch"
                        aria-checked={enabled}
                        aria-label={t('ui.damageToggle')}
                        title={t('ui.damageToggle')}
                    >
                        <span className="orbital-toggle-thumb" />
                    </button>
                </div>
            )}

            {/* Damage management section */}
            {effectivelyActive && spheres.length > 0 && (
                <>
                {/* Damage selector */}
                <div className="damage-selector">
                    <label className="damage-selector__label" htmlFor="damage-panel-select">
                        {t('markers.selectDamage')}:
                    </label>
                    <div className="damage-selector__row">
                        <select
                            id="damage-panel-select"
                            className="damage-selector__select"
                            value={selectedSphere?.name || ''}
                            onChange={handleSphereSelect}
                        >
                            <option value="">{t('markers.selectSphere')}</option>
                            {spheres.map((s) => (
                                <option key={`${s.source}-${s.index}`} value={s.name}>
                                    {s.name}
                                </option>
                            ))}
                        </select>
                        <button
                            type="button"
                            className="damage-selector__refresh"
                            onClick={handleRefresh}
                            title={t('markers.refreshList')}
                        >⟳</button>
                    </div>
                </div>

                {/* Info panel - only when a sphere is selected */}
                {selectedSphere && (
                    <div className="damage-info-panel">
                        <div className="damage-info-panel__row">
                            <span className="damage-info-panel__label">{t('markers.tableName')}:</span>
                            <span className="damage-info-panel__value">{selectedSphere.name}</span>
                        </div>
                        <div className="damage-info-panel__row">
                            <span className="damage-info-panel__label">{t('markers.tableDescription')}:</span>
                            <span className="damage-info-panel__value">{selectedSphere.description || '-'}</span>
                        </div>
                        <div className="damage-info-panel__row">
                            <span className="damage-info-panel__label">{t('markers.tableSide')}:</span>
                            <span className="damage-info-panel__value">{getSide()}</span>
                        </div>
                        {photoCount > 0 && (
                            <button
                                type="button"
                                className="damage-info-panel__photos-btn"
                                onClick={handleViewPhotos}
                            >
                                {t('gallery.viewPhotos')} ({photoCount})
                            </button>
                        )}

                        {/* Edit button */}
                        <button
                            type="button"
                            className="damage-edit-button"
                            onClick={handleEdit}
                        >
                            {t('markers.edit')}
                        </button>
                    </div>
                )}
                </>
            )}
        </div>
    );
};

export default DamageToggle;
